import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthRoute } from "@/core/components/auth-route";
import { ProtectedRoute } from "@/core/components/protected-route";
import { PublicRoute } from "@/core/components/public-route";
import { Toaster } from "@/core/components/ui/toast";
import { isProductEnabled } from "@/business/product-access";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useDocumentTitle } from "@/core/env/use-document-title";
import NavBar from "@/nav";
import NotFound from "@/not-found";
import { ProductMarketingBoundary } from "@/product-marketing/product-marketing-boundary";
import Referrals from "@/referrals";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner, isApOnly } from "@/user/utils";
import { WaitingForPhoneNumber, WaitingForSetup } from "@/waiting-pages";
import AccountsPayable from "./accounts-payable";
import AccountsPayableInvoicePage from "./accounts-payable/invoice-review-page";
import AccountsPayableReceiptPage from "./accounts-payable/receipt-review-page";
import ActivityFeed from "./activity-feed";
import AdvocateSignupRedirect from "./advocate-signup-redirect";
import BusinessSettings from "./business-settings";
import Business from "./business-settings/business";
import BusinessSettingsRedirect from "./business-settings/business-settings-redirect";
import Customers from "./business-settings/customers";
import LegacyApprovals from "./business-settings/legacy-approvals/legacy-approvals";
import Payments from "./business-settings/payments";
import { QuickbooksCallback } from "./business-settings/quickbooks-callback";
import ReferralHistory from "./business-settings/referral-history";
import ReferralSources from "./business-settings/referral-sources";
import { Reviews } from "./business-settings/reviews";
import TextMessages from "./business-settings/text-messages";
import Users from "./business-settings/users";
import { Campaigns } from "./campaigns";
import ClaimPayout from "./claim-payout";
import { useProductionWarning } from "./core/env/use-product-warning";
import { PylonProvider } from "./core/pylon";
import { usePylonAccess } from "./core/pylon/use-pylon-access";
import Dashboard from "./dashboard";
import ForgotPassword from "./forgot-password";
import GetVolca from "./get-volca";
import IcmStatementPage from "./icm/icm-statement-page";
import Login from "./login";
import { OAuthCallback } from "./oauth-callback";
import Onboarding from "./onboarding";
import { SIMPLE_ONBOARDING_PATHS } from "./onboarding/routes";
import PayrollEditorPage from "./payroll/editor/payroll-editor-page";
import PayBreakdownPage from "./payroll/pay-breakdown/pay-breakdown-page";
import PayrollStatementNotificationsPage from "./payroll/pay-breakdown/payroll-statement-notifications";
import { PAY_BREAKDOWN_PATH } from "./payroll/pay-breakdown/routes";
import PayrollRosterPage from "./payroll/roster";
import PayrollSettings from "./payroll/settings";
import {
  LEGACY_PAYROLL_EDITOR_PATH,
  PAYROLL_EDITOR_PATH,
  PAYROLL_SETTINGS_PATH,
} from "./payroll/settings/routes";
import PrivacyPolicy from "./privacy-policy";
import Profile from "./profile";
import ProfitabilityPage from "./profitability";
import ResetPassword from "./reset-password";
import ResetPasswordCode from "./reset-password-code";
import Signup from "./signup";
import Spiffs from "./spiffs";
import PayrollPeriods from "./spiffs/payroll-periods";
import SpiffPrograms from "./spiffs/spiff-programs/spiff-programs";
import TechFeedback from "./tech-feedback";
import TestError from "./test-error";

const AppContent = () => {
  const hasPylonAccess = usePylonAccess();
  useDocumentTitle();
  useProductionWarning();
  const location = useLocation();
  const isPayoutRoute = location.pathname.startsWith("/me/");
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();
  const enableAp = isProductEnabled(settings, "accounts_payable");
  const { data: user } = useGetMyUser();
  const apOnly = isApOnly(user?.role);

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col overflow-hidden">
        {!isPayoutRoute && <NavBar />}
        <main className="w-full flex-1 overflow-y-auto">
          <Routes>
            {/* Protected routes group */}
            <Route
              element={
                <ProtectedRoute>
                  <PylonProvider hasAccess={hasPylonAccess}>
                    <ProductMarketingBoundary>
                      <Outlet />
                    </ProductMarketingBoundary>
                  </PylonProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path={SIMPLE_ONBOARDING_PATHS.root}
                element={
                  <Navigate to={SIMPLE_ONBOARDING_PATHS.welcome} replace />
                }
              />
              <Route
                path={`${SIMPLE_ONBOARDING_PATHS.root}/:pane`}
                element={<Onboarding />}
              />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/profitability/:runId?"
                element={<ProfitabilityPage />}
              />
              <Route path="/activity-feed" element={<ActivityFeed />} />
              {(enableAp ||
                isProductsLoading ||
                apOnly ||
                isAdminOrOwner(user?.role)) && (
                <>
                  <Route
                    path="/accounts-payable"
                    element={<AccountsPayable />}
                  />
                  <Route
                    path="/accounts-payable/invoices/:invoiceId"
                    element={<AccountsPayableInvoicePage />}
                  />
                  <Route
                    path="/accounts-payable/receipts/:receiptId"
                    element={<AccountsPayableReceiptPage />}
                  />
                </>
              )}
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/business-settings"
                element={<BusinessSettingsRedirect />}
              />
              <Route
                path="/business-settings/:businessId"
                element={<BusinessSettings />}
              >
                <Route index element={<Business />} />
                <Route path="users/:tab?" element={<Users />} />
                <Route path="customers/:tab?" element={<Customers />} />
                <Route path="payments/:tab?" element={<Payments />} />
                <Route path="referral-history" element={<ReferralHistory />} />
                <Route path="text-messages" element={<TextMessages />} />
                <Route path="referral-sources" element={<ReferralSources />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="legacy-approvals" element={<LegacyApprovals />} />
              </Route>
              <Route path="/spiffs" element={<Spiffs />}>
                <Route index element={<PayrollPeriods />} />
                <Route path="spiff-programs" element={<SpiffPrograms />} />
              </Route>
              <Route
                path="/payroll"
                element={<Navigate to={PAY_BREAKDOWN_PATH} replace />}
              />
              <Route path={PAY_BREAKDOWN_PATH} element={<PayBreakdownPage />} />
              {/* A two-pane working surface of its own, outside Settings. */}
              <Route
                path={PAYROLL_EDITOR_PATH}
                element={<PayrollEditorPage />}
              />
              {/* Links from before the workspace left the URL, and the older
                  Settings path, both land on the one screen. */}
              <Route
                path={`${PAYROLL_EDITOR_PATH}/*`}
                element={<Navigate to={PAYROLL_EDITOR_PATH} replace />}
              />
              <Route
                path={`${LEGACY_PAYROLL_EDITOR_PATH}/*`}
                element={<Navigate to={PAYROLL_EDITOR_PATH} replace />}
              />
              <Route path={PAYROLL_SETTINGS_PATH} element={<PayrollSettings />}>
                <Route index element={<Navigate to="roster" replace />} />
                <Route path="roster" element={<PayrollRosterPage />} />
              </Route>
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/notifications`}
                element={<PayrollStatementNotificationsPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart`}
                element={<PayBreakdownPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/:section`}
                element={<PayBreakdownPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/:periodStart/statements/:statementId`}
                element={<IcmStatementPage />}
              />
              <Route
                path={`${PAY_BREAKDOWN_PATH}/statements/:statementId`}
                element={<IcmStatementPage />}
              />
            </Route>
            {/* Auth routes group (directs to app if already logged in) */}
            <Route
              element={
                <AuthRoute>
                  <Outlet />
                </AuthRoute>
              }
            >
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password-code"
                element={<ResetPasswordCode />}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            {/* Other public routes */}
            <Route
              element={
                <PublicRoute>
                  <Outlet />
                </PublicRoute>
              }
            >
              <Route path="/waiting-for-setup" element={<WaitingForSetup />} />
              <Route
                path="/waiting-for-phone-number"
                element={<WaitingForPhoneNumber />}
              />
              <Route path="/get-volca" element={<GetVolca />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/tech-feedback" element={<TechFeedback />} />
              <Route
                path="/advocate-signup-redirect"
                element={<AdvocateSignupRedirect />}
              />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route
                path="/quickbooks/callback"
                element={<QuickbooksCallback />}
              />
              <Route path="/me/:payoutEndpoint" element={<ClaimPayout />} />
              <Route path="/test-error" element={<TestError />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </main>
        <Toaster />
      </div>
    </TooltipProvider>
  );
};

export default AppContent;
